import { useNavigate } from "react-router";
import { useApiMutation } from "../../../../common/infrastructure/hooks/useApiMutation";
import { useState } from "react";
import { getStatus } from "../utils/PlannerEventStatusUtility";
import { humanizePaymentType } from "../../../../common/utilities/paymentUtility";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import { routePaths } from "../../../../common/infrastructure/routes/routePaths";
import { IPlannerEventUserModel } from "../models/plannerEventUserModel";
import { IPlannerEventModel } from "../models/plannerEventModel";
import { PaymentTypes } from "../../../../common/enums";
import { showCustomConfirmDialog } from "../../../../common/components/dialogs/AppConfirmDialog";
import { useMeetingLinkRecipientsWarning } from "../../common/hooks/useMeetingLinkRecipientsWarning";

export const UNDO_ARRIVAL_PROMPT =
  "Плащането на резервацията ще бъде премахнато, освен ако е платена през Stripe. Сигурни ли сте, че искате да върнете статуса?";

export const usePlannerEventReservationDetailsReservation = (
  user: IPlannerEventUserModel,
  isEventPassed: boolean,
  event?: IPlannerEventModel
) => {
  const { mutateAsync } = useApiMutation();
  const { warnClientsWithoutEmail } = useMeetingLinkRecipientsWarning();
  const navigate = useNavigate();

  const [isConfirmingReservation, setIsConfirmingReservation] = useState(false);
  const [isRemovingPaymentMethod, setIsRemovingPaymentMethod] = useState(false);
  const [isTogglingFormFulfillment, setIsTogglingFormFulfillment] = useState(false);
  const [addPaymentMethodUserEventId, setAddPaymentMethodUserEventId] = useState(0);

  const { color: statusColor, label: statusLabel } = getStatus(
    isEventPassed,
    user?.status,
    user?.isCancelled
  );

  const basePaymentTypeLabel = humanizePaymentType(
    user?.paymentType,
    user?.paymentPlanId,
    user?.planName
  );

  // The currency is no longer appended here — the amount itself carries it.
  const paymentTypeLabel = basePaymentTypeLabel;

  const visitsUserLabel =
    !!user?.initialPlanVisits && !!user?.visitsUsed
      ? `${user?.visitsUsed}/${user?.initialPlanVisits}`
      : "N/A";

  // Multisport payments must not reveal the paid amount to studio admins.
  const shouldShowPricePaid = user?.paymentType !== PaymentTypes.Multisport;

  const onToggleReservationConfirmation = async (onSuccessCallback?: () => void) => {
    setIsConfirmingReservation(true);
    try {
      await mutateAsync(
        {
          endpoint: `${endpointsMetadata.planner.toggleConfirmReservation}?userEventId=${user.userEventId}`,
          method: "put",
          globalIsLoading: true,
          invalidateKeys: [[endpointsMetadata.planner.getEvent]],
        },
        {
          onSuccess: () => {
            onSuccessCallback?.();
          },
          onSettled: () => {
            setIsConfirmingReservation(false);
          },
        }
      );
    } catch (error) {
      //already handled
    }
  };

  // Moving a reservation back from "Взета" also takes its payment off
  // (all but Stripe), so it is never a one-click accident.
  const onBackToConfirmedReservation = () => {
    showCustomConfirmDialog(
      UNDO_ARRIVAL_PROMPT,
      () => moveBackToConfirmedReservation(),
      () => {},
      "warning",
      "Да",
      "Не"
    );
  };

  const moveBackToConfirmedReservation = async () => {
    setIsConfirmingReservation(true);
    try {
      await mutateAsync(
        {
          endpoint: `${endpointsMetadata.planner.markUserAsNotArrived}?userEventId=${user.userEventId}`,
          method: "put",
          globalIsLoading: false,
          invalidateKeys: [[endpointsMetadata.planner.getEvent]],
        },
        {
          onSuccess: () => {},
          onSettled: () => {
            setIsConfirmingReservation(false);
          },
        }
      );
    } catch (error) {
      //already handled
    }
  };

  const onRemovePaymentMethodClick = async () => {
    setIsRemovingPaymentMethod(true);
    try {
      await mutateAsync(
        {
          endpoint: `${endpointsMetadata.planner.removePaymentMethod}?userEventId=${user.userEventId}`,
          method: "delete",
          globalIsLoading: false,
          invalidateKeys: [[endpointsMetadata.planner.getEvent]],
        },
        {
          onSettled: () => {
            setIsRemovingPaymentMethod(false);
          },
        }
      );
    } catch (error) {
      //already handled
    }
  };

  const onEventArrivalConfirmationClick = () => {
    navigate(
      routePaths.plannerEventArrivalConfirmation.replace(
        ":userEventId",
        user.userEventId?.toString()
      )
    );
  };

  const onToggleFormFulfillment = async () => {
    setIsTogglingFormFulfillment(true);
    try {
      await mutateAsync(
        {
          endpoint: `${endpointsMetadata.planner.toggleFormFulfillment}?userEventId=${user.userEventId}`,
          method: "put",
          globalIsLoading: false,
          invalidateKeys: [[endpointsMetadata.planner.getEvent]],
        },
        {
          onSuccess: () => {},
          onSettled: () => {
            setIsTogglingFormFulfillment(false);
          },
        }
      );
    } catch (error) {
      //already handled
    }
  };

  const handleConfirmReservation = () => {
    onToggleReservationConfirmation(() => {
      // Confirming emails a remote event's client the Meet link; warn when they have no email.
      if (event?.isRemote) {
        warnClientsWithoutEmail(event.id, [user.id]);
      }

      if (event?.requirePrepayment) {
        setAddPaymentMethodUserEventId(user.userEventId);
      }
    });
  };

  const closePaymentMethodDialog = () => {
    setAddPaymentMethodUserEventId(0);
  };

  const openPaymentMethodDialog = () => {
    setAddPaymentMethodUserEventId(user.userEventId);
  };

  return {
    isConfirmingReservation,
    isRemovingPaymentMethod,
    isTogglingFormFulfillment,
    statusColor,
    statusLabel,
    paymentTypeLabel,
    visitsUserLabel,
    shouldShowPricePaid,
    addPaymentMethodUserEventId,
    onEventArrivalConfirmationClick,
    onBackToConfirmedReservation,
    onToggleReservationConfirmation,
    onRemovePaymentMethodClick,
    onToggleFormFulfillment,
    handleConfirmReservation,
    closePaymentMethodDialog,
    openPaymentMethodDialog,
  };
};
